import { bindFunctions } from '@app/utils/class'
import { getRLContentContainer } from '@app/utils/window'
import classNames from 'classnames'
import { List } from 'immutable'
import * as React from 'react'
import {
  AutoSizer,
  Index,
  IndexRange,
  InfiniteLoader,
  RowMouseEventHandlerParams,
  Table,
  WindowScroller,
} from 'react-virtualized'
import './InfiniteScrollTable.scss'

interface InfiniteScrollTableProps {
  headerHeight?: number
  isLoading: boolean
  items: List<any>
  loadItems: (
    startIndex: number,
    stopIndex: number,
    append: boolean,
    loadComplete: () => void,
  ) => void
  minimumBatchSize?: number
  noRowsRenderer?: () => JSX.Element
  onRowClicked?: (item: any, index: number, event: any) => void
  overscanRowCount?: number
  rowHeight?: any
  totalItems: number
  children: React.ReactNode
}

export default class InfiniteScrollTable extends React.PureComponent<
  InfiniteScrollTableProps
> {
  private loader: InfiniteLoader | null
  private reloadTimeoutId?: number
  private scrollElement?: Element
  private table: Table | null

  constructor(props: InfiniteScrollTableProps) {
    super(props)
    bindFunctions(this)
  }

  public componentDidMount() {
    this._loadItems(0, this.props.minimumBatchSize || 20, false)
  }

  public forceReloadData() {
    if (this.reloadTimeoutId) {
      clearTimeout(this.reloadTimeoutId)
    }

    this.reloadTimeoutId = window.setTimeout(() => {
      if (this.loader) {
        this.loader.resetLoadMoreRowsCache(true)
      }
      this._loadItems(0, this.props.minimumBatchSize || 20, false).then(() => {
        if (this.table) {
          this.table.forceUpdateGrid()
        }
      })
    }, 250)
  }

  public recomputeRowHeights(index?: number) {
    if (this.table) {
      this.table.recomputeRowHeights(index)
    }
  }

  public render() {
    if (!this.scrollElement) {
      this.scrollElement = getRLContentContainer()
    }

    return (
      <WindowScroller scrollElement={this.scrollElement}>
        {({ height, isScrolling, onChildScroll, registerChild, scrollTop }) => (
          <AutoSizer disableHeight>
            {({ width }) => (
              <div ref={registerChild as any}>
                <InfiniteLoader
                  isRowLoaded={this._isRowLoaded}
                  loadMoreRows={this._loadMoreRows}
                  minimumBatchSize={this.props.minimumBatchSize || 20}
                  ref={(el) => (this.loader = el)}
                  rowCount={this.props.totalItems}
                >
                  {({ onRowsRendered, registerChild: loaderChild }) => (
                    <Table
                      autoHeight
                      headerClassName='table-list-header'
                      headerHeight={this.props.headerHeight || 30}
                      height={height || 0}
                      isScrolling={isScrolling}
                      noRowsRenderer={this.props.noRowsRenderer}
                      onRowClick={this._onRowClicked}
                      onRowsRendered={onRowsRendered}
                      onScroll={onChildScroll}
                      overscanRowCount={this.props.overscanRowCount || 10}
                      ref={(el) => {
                        this.table = el
                        loaderChild(el)
                      }}
                      rowClassName={this._getRowClassName}
                      rowCount={this.props.items.size}
                      rowGetter={this._getRow}
                      rowHeight={this.props.rowHeight || 40}
                      scrollTop={scrollTop}
                      width={width}
                    >
                      {this.props.children}
                    </Table>
                  )}
                </InfiniteLoader>
              </div>
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    )
  }

  private _getItem(index: number): any {
    return this.props.items.get(index)
  }

  private _getRow({ index }: Index): any {
    return this._getItem(index)
  }

  private _getRowClassName({ index }: Index): string {
    return classNames('table-list-row', {
      'data-row': index >= 0,
      'header-row': index === -1,
      'last-row': index === this.props.items.size - 1,
      'striped-row': index % 2 === 1,
    })
  }

  private _isRowLoaded({ index }: Index): boolean {
    if (this.props.items.size <= index) {
      return false
    }
    return !!this._getItem(index)
  }

  private _loadItems(
    startIndex: number,
    stopIndex: number,
    append: boolean,
  ): Promise<void> {
    return new Promise((resolve) => {
      this.props.loadItems(startIndex, stopIndex, append, resolve)
    })
  }

  private _loadMoreRows({ startIndex, stopIndex }: IndexRange): Promise<void> {
    if (this.props.isLoading) {
      return Promise.resolve()
    }

    return this._loadItems(startIndex, stopIndex, true)
  }

  private _onRowClicked({
    event,
    rowData,
    index,
  }: RowMouseEventHandlerParams): void {
    if (this.props.onRowClicked) {
      const item = rowData as any
      this.props.onRowClicked(item, index, event)
    }
  }
}
